import * as Yup from 'yup';
import { emailRegEx, phoneRegEx, zipCodeRegEx } from 'utils/RegularExpressions';

const defaultStepsSchema = Yup.object({
  applicantInformation: Yup.object({
    fullName: Yup.string()
      .required('Required')
      .max(250, 'Must be less than 250 characters.'),
    contactEmail: Yup.string()
      .required('Required')
      .matches(emailRegEx, 'Please enter a valid email address'),
  }),
});

const step1Section2Schema = Yup.object({
  streetAddress: Yup.string().required('Required'),
  city: Yup.string().required('Required'),
  state: Yup.string().required('Required'),
  zipCode: Yup.string()
    .typeError('Must be a number')
    .required('Required')
    .matches(zipCodeRegEx, 'Please enter a valid Zip / Postal code'),
  buildingCount: Yup.number()
    .required('Required')
    .positive('Must be positive number')
    .transform((_, val) => (val ? Number(val) : undefined)),
  currentUsage: Yup.string().required('Required'),
  zoning: Yup.string().required('Required'),
});
const step1Section1Schema = Yup.object({
  ownerType: Yup.string().required('Required'),
  buildingOwners: Yup.array()
    .of(Yup.object({ name: Yup.string().required('Required') }))
    .min(1, 'Please provide the name of at least one property owner'),
  ein: Yup.string()
    .required('Required')
    .min(9, 'EIN must be 9 digits')
    .max(9, 'EIN less than 9 digits'),
  duns: Yup.string()
    .nullable()
    .matches(/^\d{9}$/, 'DUNS must be 9 digits')
    .transform((value) => (!!value ? value : null)),
  streetAddress: Yup.string().required('Required'),
  city: Yup.string().required('Required'),
  state: Yup.string().required('Required'),
  zipCode: Yup.string()
    .typeError('Must be a number')
    .required('Required')
    .matches(zipCodeRegEx, 'Please enter a valid Zip / Postal code'),
  phone: Yup.string()
    .required('Required')
    .matches(phoneRegEx, 'Please enter a valid phone number'),
  fax: Yup.string().matches(phoneRegEx, {
    excludeEmptyString: true,
    message: 'Please enter a valid fax number',
  }),
  email: Yup.string()
    .required('Required')
    .matches(emailRegEx, 'Please enter a valid email address'),
  hasContactName: Yup.boolean(),
  additionalName: Yup.string().when('hasContactName', {
    is: true,
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.nullable(),
  }),
  hasAddress: Yup.boolean(),
  additionalAddress: Yup.string().when('hasAddress', {
    is: true,
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.nullable(),
  }),
});

const part1Schema = Yup.object({
  step1: Yup.object({
    section1: step1Section1Schema,
    section2: step1Section2Schema,
  }),
});

const indebtednessSchema = Yup.object({
  debtType: Yup.string().required('Required'),
  payableTo: Yup.string().required('Required'),
  originalAmount: Yup.number().required('Required'),
  originalDate: Yup.string()
    .required('Required')
    .test('date', `Please select past date`, (val) => {
      if (val) {
        return new Date(val).valueOf() <= new Date().valueOf();
      }
      return true;
    }),
  presentBalance: Yup.number().required('Required'),
  interestRate: Yup.number().required('Required'),
  maturityDate: Yup.string()
    .required('Required')
    .test('date', `Please select future date`, (val) => {
      if (val) {
        return new Date(val).valueOf() >= new Date().valueOf();
      }
      return true;
    }),
  monthlyPayment: Yup.number().required('Required'),
});

const tenancySchema = Yup.object({
  unitNoAndName: Yup.string().required('Required'),
  type: Yup.string().required('Required'),
  currentRent: Yup.number().required('Required'),
  willIncreasedRent: Yup.boolean().required('Required'),
  newRent: Yup.number().when('isIncreasedRent', {
    is: true,
    then: (schema) => schema.required(),
  }),
});

const part2Schema = Yup.object({
  financialInfo: Yup.object({
    indebtedness: Yup.array().of(indebtednessSchema),
    tenancy: Yup.array().of(tenancySchema),
  }),
});

const proposedFinanceSchema = Yup.object({
  source: Yup.string().required('Required'),
  rate: Yup.number().required('Required'),
  term: Yup.number().required('Required'),
  type: Yup.string().required('Required'),
  eligibleCost: Yup.number().required('Required'),
  ineligibleCost: Yup.number().required('Required'),
  totalCost: Yup.number().required('Required'),
});

const part3Schema = Yup.object({
  step3: Yup.object({
    projectDescription: Yup.string().required('Required'),
    beginDate: Yup.string()
      .required('Required')
      .test({
        message: 'It must be earlier than completion date',
        test: (val, ctx) => {
          if (ctx.parent.completionDate && val) {
            return new Date(val) < new Date(ctx.parent.completionDate);
          }
          return true;
        },
      }),
    completionDate: Yup.string()
      .required('Required')
      .test({
        message: 'It must be later than start date',
        test: (val, ctx) => {
          if (ctx.parent.beginDate && val) {
            return new Date(val) > new Date(ctx.parent.beginDate);
          }
          return true;
        },
      }),
    hasPortion: Yup.string().required('Required'),
    portionDescription: Yup.string().when('hasPortion', {
      is: 'yes',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
    section2: Yup.array().of(proposedFinanceSchema),
  }),
});

const part4Schema = Yup.object({
  step4: Yup.object({
    section1: Yup.object({
      createNewJob: Yup.string(),
      jobClassification: Yup.array(),
      willResultRhu: Yup.string().required('Required'),
      rhuCount: Yup.number().when('willResultRhu', {
        is: 'yes',
        then: (schema) => schema.required('Required'),
        otherwise: (schema) => schema.nullable(),
      }),
      affordableHousing: Yup.string().required('Required'),
      ahCount: Yup.number().when('affordableHousing', {
        is: 'yes',
        then: (schema) =>
          schema.required('Required').test({
            message: 'It cannot be greater than Resident Housing Units',
            test: (val, ctx) => {
              if (ctx.parent.rhuCount && val) {
                return val - 1 < ctx.parent.rhuCount;
              }
              return true;
            },
          }),
        otherwise: (schema) => schema.nullable(),
      }),
    }),
    section2: Yup.object({
      description1: Yup.string(),
      description2: Yup.string(),
      description3: Yup.string(),
    }),
    section3: Yup.object({
      q1: Yup.string().required('Required'),
      q2: Yup.string().required('Required'),
      q3: Yup.string().required('Required'),
      q4: Yup.string().required('Required'),
      q5: Yup.string().required('Required'),
    }),
    section4: Yup.object({
      advDisadvantagedBusiness: Yup.string().required('Required'),
      description: Yup.string().when('advDisadvantagedBusiness', {
        is: 'yes',
        then: (schema) => schema.required('Required'),
        otherwise: (schema) => schema.nullable(),
      }),
    }),
  }),
});

const part5Schema = Yup.object({
  step5: Yup.object({
    f1: Yup.object({
      selectedFiles: Yup.array().when('haveFiles', {
        is: false,
        then: (schema) => schema.min(1, 'Provide atleast one file.'),
      }),
      haveFiles: Yup.boolean(),
      haveFilesDescription: Yup.string().when('haveFiles', {
        is: true,
        then: (schema) => schema.required('Required'),
        otherwise: (schema) => schema.nullable(),
      }),
    }),
    f2: Yup.object({
      selectedFiles: Yup.array().when('haveFiles', {
        is: false,
        then: (schema) => schema.min(1, 'Provide atleast one file.'),
      }),
      haveFiles: Yup.boolean(),
      haveFilesDescription: Yup.string().when('haveFiles', {
        is: true,
        then: (schema) => schema.required('Required'),
        otherwise: (schema) => schema.nullable(),
      }),
    }),
    f3: Yup.object({
      selectedFiles: Yup.array().when('haveFiles', {
        is: false,
        then: (schema) => schema.min(1, 'Provide atleast one file.'),
      }),
      haveFiles: Yup.boolean(),
      haveFilesDescription: Yup.string().when('haveFiles', {
        is: true,
        then: (schema) => schema.required('Required'),
        otherwise: (schema) => schema.nullable(),
      }),
    }),
    f4: Yup.object({
      selectedFiles: Yup.array().when('haveFiles', {
        is: false,
        then: (schema) => schema.min(1, 'Provide atleast one file.'),
      }),
      haveFiles: Yup.boolean(),
      haveFilesDescription: Yup.string().when('haveFiles', {
        is: true,
        then: (schema) => schema.required('Required'),
        otherwise: (schema) => schema.nullable(),
      }),
    }),
  }),
});
const part6Schema = Yup.object({
  step6: Yup.object({
    printName: Yup.string().required('Required'),
    applicantTitle: Yup.string(),
  }),
});
export const validationSchema = [
  defaultStepsSchema,
  part1Schema,
  part2Schema,
  part3Schema,
  part4Schema,
  part5Schema,
  part6Schema,
];
